import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Download, ExternalLink, Plus, ShoppingBag, Trash2 } from 'lucide-react'
import { api, type AffiliateCategory, type AffiliateHotPost, type AffiliateOverview, type Target } from '@/api/client'
import { intervalLabel, POLL_INTERVALS, SIGNAL_KINDS, signalLabel, signalTone } from '@/lib/coupang'
import { qk, useAffiliateCategories, useAffiliateOverview, useTargets } from '@/lib/queries'
import { formatRelative } from '@/lib/time'
import {
  Button,
  ConfirmDialog,
  DataTable,
  Dialog,
  EmptyState,
  ErrorState,
  IconButton,
  Input,
  PageHeader,
  Select,
  StatTile,
  TonePill,
  When,
  errorText,
  useToast,
  type Column,
} from '@/ui'

export const LIMIT_MIN = 1
export const LIMIT_MAX = 100

/** Writes to the category list, then reads the list and the tiles again. */
function useAffiliateWrites() {
  const qc = useQueryClient()
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: qk.affiliateCategories })
    void qc.invalidateQueries({ queryKey: qk.affiliateOverview })
  }
  return { refresh }
}

/** /affiliate: what is collected from Coupang, from which categories, and for
 *  which account. The posts themselves come from campaigns in mode 쿠팡. */
export default function Affiliate() {
  const overview = useAffiliateOverview()
  const cats = useAffiliateCategories()
  const [adding, setAdding] = useState(false)
  const items = cats.data?.items
  const catalog = cats.data?.catalog ?? []
  const left = catalog.filter((c) => !items?.some((i) => i.coupang_cat_id === c.id))

  return (
    <>
      <PageHeader
        title="쿠팡"
        description="쿠팡 베스트셀러를 모아 가격이 움직인 상품을 찾는다. 글은 모드가 쿠팡인 캠페인이 쓴다."
        actions={
          <Button variant="primary" icon={Plus} onClick={() => setAdding(true)} disabled={!cats.data}>
            카테고리 추가
          </Button>
        }
      />
      <div className="space-y-6">
        <Tiles overview={overview.data} error={overview.error} onRetry={() => overview.refetch()} />
        {overview.data?.hot && <HotPosts hot={overview.data.hot} />}
        <section aria-label="카테고리" className="space-y-2">
          <h2 className="text-section font-semibold">카테고리</h2>
          <CategoryTable
            items={items}
            loading={cats.isPending}
            error={cats.error}
            onRetry={() => cats.refetch()}
            keysConfigured={overview.data?.keys_configured ?? true}
            onAdd={() => setAdding(true)}
          />
        </section>
      </div>
      {adding && <AddCategoryDialog available={left} onClose={() => setAdding(false)} />}
    </>
  )
}

// ── tiles ──────────────────────────────────────────────────────────────────

function Tiles({ overview: o, error, onRetry }: { overview?: AffiliateOverview; error: unknown; onRetry: () => void }) {
  if (error) return <ErrorState title="요약을 불러오지 못했다" error={error} onRetry={onRetry} />
  const signals = o ? SIGNAL_KINDS.reduce((n, k) => n + (o.signals_24h[k] ?? 0), 0) : 0
  const run = o?.last_poll_run
  const failed = run?.status === 'error'
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatTile
          label="쿠팡 키"
          value={o ? (o.keys_configured ? '입력됨' : '없음') : '—'}
          tone={o && !o.keys_configured ? 'publishing' : undefined}
          hint={o && !o.keys_configured ? '설정에서 입력한다' : '설정에서 바꾼다'}
          to="/settings?tab=coupang"
        />
        <StatTile
          label="상품"
          value={o ? o.products.toLocaleString('ko-KR') : '—'}
          hint={o ? `수집 중인 카테고리 ${o.categories.enabled}/${o.categories.total}` : undefined}
        />
        <StatTile
          label="시그널 · 24시간"
          value={o ? signals : '—'}
          hint={o ? `아직 안 쓴 것 ${o.unclaimed_fresh}` : undefined}
        />
        <StatTile
          label="마지막 수집"
          value={o?.last_polled_at ? formatRelative(o.last_polled_at) : '—'}
          tone={failed ? 'failed' : undefined}
          hint={failed ? run?.message || '실패' : run ? '정상' : o && !o.last_polled_at ? '아직 없음' : undefined}
          to="/jobs"
        />
      </div>
      {o && signals > 0 && (
        <ul className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-table" aria-label="시그널 종류별 개수">
          {SIGNAL_KINDS.map((k) => (
            <li key={k} className="flex items-center gap-1.5">
              <TonePill tone={signalTone(k)} label={signalLabel(k)} />
              <span className="tabular-nums">{o.signals_24h[k] ?? 0}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

// ── hot posts ──────────────────────────────────────────────────────────────

/** Posts whose views reached the bar. While one is running, its campaign brings its
 *  scheduled posts in the category forward and picks that category first. */
function HotPosts({ hot }: { hot: NonNullable<AffiliateOverview['hot']> }) {
  const now = new Date()
  return (
    <section aria-label="조회 급등" className="space-y-2">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <h2 className="text-section font-semibold">조회 급등</h2>
        <p className="text-meta text-muted-foreground">
          {hot.min_views > 0 ? (
            <>
              글 하나가 게시 후 24시간 안에 조회 {hot.min_views}회 이상이 되면, 같은 계정이 {hot.hours}시간 동안 그 카테고리 글을 먼저 올린다.{' '}
              {(hot.reposts ?? 0) > 0 && (
                <>그 상품은 다음 날 글을 살짝 바꿔 다시 올린다(상품당 {hot.reposts}번까지, 다시 올린 글도 급등해야 또 올린다).{' '}</>
              )}
            </>
          ) : (
            <>꺼져 있다. </>
          )}
          <Link to="/settings?tab=coupang.signals" className="text-accent hover:underline">
            기준 바꾸기
          </Link>
        </p>
      </div>
      {hot.items.length === 0 ? (
        <p className="text-table text-muted-foreground">최근 3일 동안 급등한 글이 없다.</p>
      ) : (
        <ul className="divide-y divide-border rounded-lg border border-border bg-card">
          {hot.items.map((h) => (
            <HotRow key={h.post_id} h={h} running={new Date(h.until) > now} />
          ))}
        </ul>
      )}
    </section>
  )
}

function HotRow({ h, running }: { h: AffiliateHotPost; running: boolean }) {
  return (
    <li className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2.5 text-table">
      <TonePill tone={running ? 'scheduled' : 'neutral'} label={running ? '우선 중' : '끝남'} />
      <span className="min-w-0 flex-1 basis-48">
        <span className="font-medium">{h.product}</span>
        <span className="block text-meta text-muted-foreground">
          {h.campaign} · {h.category || '카테고리 없음'} · {formatRelative(h.detected_at)}
        </span>
      </span>
      <span className="tabular-nums">조회 {h.views.toLocaleString('ko-KR')}</span>
      <span className="text-muted-foreground">{h.moved > 0 ? `예약 ${h.moved}건 앞당김` : '앞당긴 예약 없음'}</span>
      {h.url && (
        <a href={h.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-accent hover:underline">
          글 <ExternalLink className="h-3.5 w-3.5" aria-hidden />
        </a>
      )}
    </li>
  )
}

// ── categories ─────────────────────────────────────────────────────────────

/** The Threads accounts a category can belong to. A category kept for an
 *  account that has since been turned off still shows it. */
function accountsFor(targets: Target[], keep: number | null): Target[] {
  return targets.filter((t) => t.platform === 'threads' && (t.status === 'active' || t.id === keep))
}

function LimitInput({ cat, onSave, disabled }: { cat: AffiliateCategory; onSave: (n: number) => void; disabled: boolean }) {
  const [text, setText] = useState(String(cat.poll_limit))
  const [prev, setPrev] = useState(cat.poll_limit)
  if (prev !== cat.poll_limit) {
    setPrev(cat.poll_limit)
    setText(String(cat.poll_limit))
  }
  const n = Number(text)
  const valid = Number.isInteger(n) && n >= LIMIT_MIN && n <= LIMIT_MAX
  const commit = () => {
    if (!valid) {
      setText(String(cat.poll_limit))
      return
    }
    if (n !== cat.poll_limit) onSave(n)
  }
  return (
    <Input
      type="number"
      inputMode="numeric"
      min={LIMIT_MIN}
      max={LIMIT_MAX}
      aria-label={`${cat.name} 수집 개수`}
      aria-invalid={!valid}
      title={`한 번에 읽을 상품 수, ${LIMIT_MIN}에서 ${LIMIT_MAX}`}
      value={text}
      disabled={disabled}
      onChange={(e) => setText(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur()
      }}
      className="h-8 w-20 text-right text-table tabular-nums"
    />
  )
}

function CategoryTable({
  items,
  loading,
  error,
  onRetry,
  keysConfigured,
  onAdd,
}: {
  items?: AffiliateCategory[]
  loading: boolean
  error: unknown
  onRetry: () => void
  keysConfigured: boolean
  onAdd: () => void
}) {
  const targets = useTargets().data ?? []
  const toast = useToast()
  const { refresh } = useAffiliateWrites()
  const [removing, setRemoving] = useState<AffiliateCategory | null>(null)

  const update = useMutation({
    mutationFn: (v: { id: number; patch: Parameters<typeof api.updateAffiliateCategory>[1] }) =>
      api.updateAffiliateCategory(v.id, v.patch),
    onSuccess: refresh,
    onError: (e) => toast.error(errorText(e)),
  })
  const poll = useMutation({
    mutationFn: (c: AffiliateCategory) => api.pollAffiliateCategory(c.id),
    onSuccess: (r) => {
      toast.success(
        r.changed > 0 ? `${r.category.name}: 상품 ${r.changed}개의 가격이 달라졌다` : `${r.category.name}: 바뀐 상품이 없다`,
      )
      refresh()
    },
    onError: (e) => toast.error(errorText(e)),
    onSettled: refresh,
  })
  const remove = useMutation({
    mutationFn: (c: AffiliateCategory) => api.deleteAffiliateCategory(c.id),
    onSuccess: () => {
      refresh()
      toast.success('뺐다')
    },
  })

  const columns: Column<AffiliateCategory>[] = [
    {
      key: 'name',
      header: '카테고리',
      card: 'title',
      cell: (c) => (
        <div className="min-w-0">
          <span className="font-medium">{c.name}</span>
          <div className="text-meta font-normal text-muted-foreground">
            {c.coupang_cat_id} · 상품 {c.products.toLocaleString('ko-KR')}
          </div>
        </div>
      ),
    },
    {
      key: 'enabled',
      header: '수집',
      card: 'meta',
      width: '72px',
      cell: (c) => (
        <label className="inline-flex items-center gap-2 text-table">
          <input
            type="checkbox"
            className="h-4 w-4 accent-[rgb(var(--accent))]"
            aria-label={`${c.name} 수집`}
            checked={c.enabled}
            disabled={update.isPending && update.variables?.id === c.id}
            onChange={(e) => update.mutate({ id: c.id, patch: { enabled: e.target.checked } })}
          />
          <span className="sm:hidden">{c.enabled ? '켜짐' : '꺼짐'}</span>
        </label>
      ),
    },
    {
      key: 'account',
      header: '계정',
      cell: (c) => (
        <Select
          aria-label={`${c.name} 담당 계정`}
          value={c.default_target_id == null ? '' : String(c.default_target_id)}
          onChange={(e) =>
            update.mutate({ id: c.id, patch: { default_target_id: e.target.value === '' ? null : Number(e.target.value) } })
          }
          className="h-8 w-44 max-w-full text-table"
        >
          <option value="">두 계정 함께</option>
          {accountsFor(targets, c.default_target_id).map((t) => (
            <option key={t.id} value={String(t.id)}>
              {t.name}
            </option>
          ))}
        </Select>
      ),
    },
    {
      key: 'limit',
      header: '개수',
      align: 'right',
      cell: (c) => <LimitInput cat={c} disabled={update.isPending && update.variables?.id === c.id} onSave={(n) => update.mutate({ id: c.id, patch: { poll_limit: n } })} />,
    },
    {
      key: 'interval',
      header: '간격',
      cell: (c) => (
        <Select
          aria-label={`${c.name} 수집 간격`}
          value={String(c.poll_interval_seconds)}
          onChange={(e) => update.mutate({ id: c.id, patch: { poll_interval_seconds: Number(e.target.value) } })}
          className="h-8 w-28 text-table"
        >
          {!POLL_INTERVALS.some((i) => i.seconds === c.poll_interval_seconds) && (
            <option value={String(c.poll_interval_seconds)}>{intervalLabel(c.poll_interval_seconds)}</option>
          )}
          {POLL_INTERVALS.map((i) => (
            <option key={i.seconds} value={String(i.seconds)}>
              {i.label}
            </option>
          ))}
        </Select>
      ),
    },
    {
      key: 'last',
      header: '마지막 수집',
      cell: (c) =>
        c.last_polled_at ? <When iso={c.last_polled_at} mode="relative" className="whitespace-nowrap" /> : <span className="text-muted-foreground">아직 없음</span>,
    },
    {
      key: 'actions',
      header: <span className="sr-only">동작</span>,
      align: 'right',
      card: 'actions',
      cell: (c) => (
        <div className="flex items-center justify-end gap-1">
          <Button
            size="sm"
            variant="ghost"
            icon={Download}
            loading={poll.isPending && poll.variables?.id === c.id}
            disabled={!keysConfigured}
            title={keysConfigured ? '이 카테고리를 지금 읽는다' : '쿠팡 키를 먼저 넣는다'}
            onClick={() => poll.mutate(c)}
          >
            지금 수집
          </Button>
          <IconButton label={`${c.name} 빼기`} icon={Trash2} size="sm" onClick={() => setRemoving(c)} />
        </div>
      ),
    },
  ]

  return (
    <>
      {!keysConfigured && items && items.length > 0 && (
        <p role="status" className="mb-2 rounded-lg border border-border bg-muted/50 px-4 py-3 text-table">
          쿠팡 키가 없어 수집이 멈춰 있다. <Link to="/settings?tab=coupang" className="text-accent hover:underline">설정에서 키를 넣는다.</Link>
        </p>
      )}
      <DataTable
        label="쿠팡 카테고리"
        columns={columns}
        rows={items}
        loading={loading}
        error={error}
        onRetry={onRetry}
        rowKey={(c) => c.id}
        rowClassName={(c) => (c.enabled ? undefined : 'opacity-60')}
        empty={
          <EmptyState
            icon={ShoppingBag}
            title="수집할 카테고리가 없다"
            description="카테고리를 더하면 간격마다 그 카테고리의 베스트셀러를 읽고 가격이 움직인 상품을 기록한다."
            actions={
              <Button variant="primary" icon={Plus} onClick={onAdd}>
                카테고리 추가
              </Button>
            }
          />
        }
      />
      <ConfirmDialog
        open={removing != null}
        onOpenChange={(o) => !o && setRemoving(null)}
        title={`${removing?.name ?? ''} 카테고리를 뺄까`}
        description="더는 읽지 않는다. 이미 모은 상품과 시그널은 남고, 캠페인의 카테고리 선택에서는 사라진다."
        body={remove.error != null ? <p className="text-table text-status-failed">{errorText(remove.error)}</p> : undefined}
        confirmLabel="빼기"
        confirmVariant="destructive"
        onConfirm={async () => {
          if (!removing) return
          try {
            await remove.mutateAsync(removing)
            setRemoving(null)
          } catch {
            /* shown in the dialog */
          }
        }}
      />
    </>
  )
}

// ── add ────────────────────────────────────────────────────────────────────

function AddCategoryDialog({ available, onClose }: { available: { id: number; name: string }[]; onClose: () => void }) {
  const toast = useToast()
  const { refresh } = useAffiliateWrites()
  const [picked, setPicked] = useState<number[]>([])
  const add = useMutation({
    mutationFn: async (ids: number[]) => {
      for (const id of ids) await api.addAffiliateCategory(id)
    },
    onSettled: refresh,
  })
  const toggle = (id: number) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]))
  return (
    <Dialog
      open
      onClose={onClose}
      size="lg"
      title="카테고리 추가"
      description="쿠팡 파트너스의 대분류에서 고른다. 추가하면 한 시간마다 읽기 시작한다."
      footer={
        <>
          <Button onClick={onClose}>취소</Button>
          <Button
            variant="primary"
            loading={add.isPending}
            disabled={picked.length === 0}
            onClick={async () => {
              try {
                await add.mutateAsync(picked)
                toast.success(`${picked.length}개를 더했다`)
                onClose()
              } catch {
                /* shown below */
              }
            }}
          >
            {picked.length > 0 ? `${picked.length}개 추가` : '추가'}
          </Button>
        </>
      }
    >
      {available.length === 0 ? (
        <p className="text-body text-muted-foreground">고를 수 있는 카테고리를 모두 더했다.</p>
      ) : (
        <ul className="grid max-h-[50vh] gap-1 overflow-y-auto sm:grid-cols-2" aria-label="추가할 카테고리">
          {available.map((c) => (
            <li key={c.id}>
              <label className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-body hover:bg-muted/60">
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-[rgb(var(--accent))]"
                  checked={picked.includes(c.id)}
                  onChange={() => toggle(c.id)}
                />
                {c.name}
                <span className="ml-auto text-meta text-muted-foreground">{c.id}</span>
              </label>
            </li>
          ))}
        </ul>
      )}
      {add.error != null && <p className="mt-3 text-table text-status-failed">{errorText(add.error)}</p>}
    </Dialog>
  )
}
